export type MaintenanceFrequencyOption = 'monthly' | 'quarterly' | 'biannual' | 'annual';

export interface MaintenanceChecklistTemplateItem {
  id: string;
  label: string;
}

export interface MaintenanceChecklistTemplateGroup {
  id: string;
  title: string;
  items: MaintenanceChecklistTemplateItem[];
}

export interface MaintenanceCatalogItemTemplate {
  id: string;
  title: string;
  description: string;
  frequencyOptions: MaintenanceFrequencyOption[];
}

export const MAINTENANCE_FREQUENCY_LABELS: Record<MaintenanceFrequencyOption, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  biannual: 'Biannual',
  annual: 'Annual',
};

export const MAINTENANCE_CHECKLIST_TEMPLATE: MaintenanceChecklistTemplateGroup[] = [
  {
    id: 'arrival-setup',
    title: 'Arrival & Setup',
    items: [
      { id: 'arrival-greeting', label: 'Greet homeowner and confirm visit scope' },
      { id: 'arrival-safety', label: 'Confirm access and safety conditions' },
      { id: 'arrival-tools', label: 'Prepare tools, PPE, and walkthrough plan' },
    ],
  },
  {
    id: 'systems-walkthrough',
    title: 'Systems Walkthrough',
    items: [
      { id: 'systems-hvac-check', label: 'Inspect HVAC filters, vents, and operation' },
      { id: 'systems-plumbing-check', label: 'Check visible plumbing fixtures and shutoffs' },
      { id: 'systems-electrical-check', label: 'Review panel area and test key switches/outlets' },
    ],
  },
  {
    id: 'wrap-up',
    title: 'Wrap-Up',
    items: [
      { id: 'wrap-up-photos', label: 'Capture completion photos for major findings' },
      { id: 'wrap-up-summary', label: 'Review findings with homeowner or leave summary notes' },
      { id: 'wrap-up-next-steps', label: 'Confirm follow-up tasks and maintenance recommendations' },
    ],
  },
];

export const MAINTENANCE_CATALOG_TEMPLATE: MaintenanceCatalogItemTemplate[] = [
  {
    id: 'hvac-filter',
    title: 'HVAC filter replacement',
    description: 'Regularly replace return air filters to protect airflow and equipment life.',
    frequencyOptions: ['monthly', 'quarterly', 'biannual', 'annual'],
  },
  {
    id: 'water-heater-flush',
    title: 'Water heater flush',
    description: 'Flush tank sediment to improve heating performance and extend heater lifespan.',
    frequencyOptions: ['quarterly', 'biannual', 'annual'],
  },
  {
    id: 'smoke-detector-test',
    title: 'Smoke/CO detector test',
    description: 'Test alarms and replace batteries when needed.',
    frequencyOptions: ['monthly', 'quarterly', 'biannual', 'annual'],
  },
  {
    id: 'gutter-cleaning',
    title: 'Gutter cleaning',
    description: 'Remove debris and verify downspout drainage to prevent water damage.',
    frequencyOptions: ['quarterly', 'biannual', 'annual'],
  },
  {
    id: 'dryer-vent-cleaning',
    title: 'Dryer vent cleaning',
    description: 'Clear lint buildup in vent line to reduce fire risk and improve efficiency.',
    frequencyOptions: ['quarterly', 'biannual', 'annual'],
  },
  {
    id: 'irrigation-check',
    title: 'Irrigation system check',
    description: 'Check zones, heads, and controller schedules for leaks and coverage.',
    frequencyOptions: ['monthly', 'quarterly', 'biannual'],
  },
];
